import React, { memo } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Section } from '../components/Section';
import { SectionHeader } from '../components/SectionHeader';
import { LimeBandCTA } from '../components/LimeBandCTA';
import { ApproachCard } from '../components/ApproachCard';
export function Purpose() {
  return <div className="flex flex-col min-h-screen w-full bg-white" data-id="element-659">
      <Navbar data-id="element-660" />
      <main className="flex-grow" data-id="element-661">
        {/* Hero Section */}
        <Section className="pt-32 md:pt-48" data-id="element-662">
          <div className="flex flex-col md:flex-row md:gap-16 lg:gap-32" data-id="element-663">
            <div className="mb-8 md:mb-0 md:w-1/2" data-id="element-664">
              <h1 className="font-poppins font-normal text-3xl md:text-5xl text-black leading-tight mb-6" data-id="element-665">
                Say less. Mean more.
              </h1>
              <div className="space-y-4" data-id="element-666">
                <p className="font-inter text-base leading-relaxed text-black" data-id="element-667">
                  Most companies aren't short on things to say; they're
                  competing for a reason people should care. When positioning
                  lacks a point of view, messages drift, and attention goes with
                  them. Too many stories told too many ways leave buyers
                  wondering, "Why you?" In a world of fractured focus and
                  one‑click swaps, that's not just a marketing miss, it's a
                  business risk.
                </p>
              </div>
            </div>
            <div className="md:w-1/2 overflow-hidden rounded-lg aspect-[4/3]" data-id="element-668">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3gFHrGMqNbU1XZsWUSK6BD/Purpose_Image_1.jpg" alt="Hexagonal architectural pattern with natural light" className="w-full h-full object-cover" data-id="element-669" />
            </div>
          </div>
        </Section>
        {/* POV Section */}
        <Section data-id="element-670">
          <div className="max-w-4xl mx-auto text-center" data-id="element-671">
            <h2 className="font-poppins font-normal text-3xl md:text-4xl text-black mb-6" data-id="element-672">
              Our take.
            </h2>
            <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-673">
              In fast‑moving, matrixed companies, messaging gets messy -
              fragmented across teams, global by default (not by design), and
              built from the inside out. The result? Stories that are hard to
              tell, harder to scale, and easy to forget. Positioning can't just
              be a paragraph on a page; it has to be a living system that links
              what you stand for to the market you want to move. It should flex
              without falling apart, whether it's in a board deck, a banner ad,
              or your SDR's first line on LinkedIn.
            </p>
          </div>
        </Section>
        {/* Solution Section */}
        <Section bgColor="lightgray" data-id="element-674">
          <div className="flex flex-col md:flex-row md:gap-16 lg:gap-32" data-id="element-675">
            <div className="mb-8 md:mb-0 md:w-1/2 bg-white overflow-hidden rounded-lg flex items-center justify-center aspect-[4/3]" data-id="element-676">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wEZn4BSeBcosWNFTu2cXt1/Purpose_Image_2.jpg" alt="Geometric diamond pattern with textured surface" className="w-full h-full object-cover" data-id="element-677" />
            </div>
            <div className="md:w-1/2 flex flex-col justify-center" data-id="element-678">
              <h2 className="font-poppins font-normal text-2xl md:text-3xl text-black mb-4" data-id="element-679">
                Working from the outside in.
              </h2>
              <div className="space-y-4" data-id="element-680">
                <p className="font-inter text-base leading-relaxed text-black" data-id="element-681">
                  Positioning should build conviction around what you stand for
                  and create the structure that helps teams express it. Clearly,
                  consistently, and with context. It should be big enough for
                  the brand, clear enough for the market, and flexible enough
                  for execs to lead with it, sellers to pitch it, and partners
                  to amplify it.
                </p>
                <p className="font-inter text-base leading-relaxed text-black" data-id="element-682">
                  That's why we don't just deliver a positioning statement. We
                  build the system that helps it scale.
                </p>
              </div>
            </div>
          </div>
        </Section>
        {/* Approach Section */}
        <Section data-id="element-683">
          <SectionHeader title="Our Approach" centered data-id="element-684" />
          <div className="space-y-12 md:space-y-16" data-id="element-685">
            <ApproachCard title="Insight that goes deeper." description="We combine internal interviews, customer conversations, analyst insights, competitor audits, and audience research to build a complete picture of where you stand and where you can win. We don't just ask stakeholders what they think; we layer it with what buyers are searching for, what competitors are saying, and what the market actually cares about. AI helps us move faster, cover more ground, and keep the analysis sharp and unbiased." imagePosition="right" imageLabel="Purpose Image 3" data-id="element-686" />
            <ApproachCard title="A point of view you can defend." description="We don't stop at a positioning statement. We build a clear, defensible point of view, and structure it so it works across the business, from brand anthems and product marketing to sales pitches and investor decks. Every recommendation is tied back to the research, so teams stay aligned on what matters, why, and how to back it up." imagePosition="left" imageLabel="Purpose Image 4" data-id="element-687" />
            <ApproachCard title="Positioning you can use." description="We don't just hand over slides and walk away. We bring the positioning to life through a core asset (like a pitch deck, narrative, or even a playbook) so teams can see it in action. Then we help roll it out with live sessions, Q&As, and tools that make it real for the people who need to use it every day." imagePosition="right" imageLabel="Purpose Image 5" data-id="element-688" />
          </div>
        </Section>
        {/* Closing Section */}
        <Section bgColor="lightgray" data-id="element-689">
          <div className="max-w-4xl mx-auto text-center" data-id="element-690">
            <h2 className="font-poppins font-normal text-3xl md:text-4xl text-black mb-6" data-id="element-691">
              Positioning people believe in, and use.
            </h2>
            <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-692">
              Your positioning shouldn't be something people have to memorize.
              Or just a paragraph in a pitch deck. It should be something they
              believe in, and know how to use. We help you get there. With
              positioning that gives teams the clarity to align, the structure
              to scale, and the confidence to show up in the market like they
              mean it.
            </p>
          </div>
        </Section>
        {/* CTA Band */}
        <LimeBandCTA text="Ready to get started?" linkText="Let's Connect" href="#" data-id="element-693" />
      </main>
      <Footer data-id="element-694" />
    </div>;
}